import Vue from 'vue';
import { modulo } from '@/core';

if (!window.__focusBoundaries) {
	window.__focusBoundaries = {};
}

/*
This directive makes the bound element override normal tabbing behaviour.
It will listen keydown events occuring inside it and prevent them from propagating.
It also disables default behaviour of the tab key, instead the focusNext-method is used.
The focusNext-method is an implementation of tab that acts as a opinionated user agent
_mostly_ implementing the HTML standard and introducing some new concepts:
	Focus boundary: A DOM element that controls the tabbing inside it (the element with this directive)
					A focus boundary can contain another focus boundary.
					The outer boundary will be able to tab into inner boundaries.
					When pressing tab the first ancestor of the active element that is a focus boundary is given control.
					When reaching past the end of the boundary control is handed over to the outer boundary.
	Previous/Next boundary: Where to continue tabbing when exiting one focus boundary. Used to override default behaviour.
	No-entry boundary: A boundary that will be skipped by outer boundaries, but where tabbing works as normally inside it.
	Circular boundary: A boundary that never hands over control to outer boundaries, but instead starts over when reaching the end.

Catches:
	-	A user-agent that relies only on keyboard (and thus let's the user tab into elements with tabindex = -1)
		will not behave as intended within a focus boundary.
	-	Tabindex > 0 will behave like tabindex=0 for normally focusable elements and will be ignored for other elements (e.g. div).
	-	Not all elements that the HTML spec says should be tabable by default are indeed tabable by default here. See below.

These elements are tabable by default: input, button, textarea, a, select.
These elements should also be tabable by default, according to w3 HTML spec: area, frame, iframe, object and SVGa.
The reason these elements are omitted is because they don't seem to be used in this project.
*/

export default {
	bind(el, binding) {
		if (binding.value && binding.value.id) {
			registerBoundary(binding.value.id, el);
		}
	},

	inserted(el, binding) {
		let { id, prev, next } = binding.value || {};

		let { previousBoundary, nextBoundary } = prev || next ? getAdjacentBoundaries(prev, next) : {};
		let options = { ...binding.modifiers, previousBoundary, nextBoundary, id };

		// Binding el to first argument: focusBase
		el.addEventListener('keydown', onKeyDown.bind(null, el));
		el.focusNext = unbound_focusNext.bind(null, el, options);
		el.focusFirst = unbound_focusFirst.bind(null, el);
		el.focusLast = unbound_focusLast.bind(null, el);
	},

	// This hook is called after children has been updated.
	// However, newly added components are not mounted, so we must use nextTick.
	componentUpdated(el, binding) {
		if (binding.modifiers.noEntry) {
			// TODO: See if there is a way to only do this when needed (i.e. when a new child is added)
			Vue.nextTick(() => {
				setNoEntryAttributes(el);
			});
		}
	},

	unbind(el, binding) {
		if (binding.value && binding.value.id) {
			unregisterBoundary(binding.value.id);
		}
	},
};


// Selects elements with tag input, button, textarea, a or select
// that is not disabled or has tabindex = -1
// and does not have attribute data-focus-no-entry (true for elements within a focus-boundary.no-entry)
// or any element with tabindex = 0.
// Note that this differs slightly from normal HTML behaviour. See the top of the file for more information.
const experimentalQueryString = ':is(input, button, textarea, a, select):not([disabled]):not([tabindex="-1"]), *[tabindex="0"]';
const polyfillQueryString = 'input:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), a:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), *[tabindex="0"]';

let queryString = experimentalQueryString;
try {
	document.body.querySelector(queryString);
} catch (err) {
	queryString = polyfillQueryString;
}

// First parameter, focusBase should be bound to the DOM-element that defines the focus boundary.
// Second parameter are options that should be bound when mounted.
// Third parameter are "runtime" options. An event object is a suitable argument for this.
const unbound_focusNext = (focusBase,
	{ circular=false, nextBoundary, previousBoundary },
	{ shiftKey=false, target=document.activeElement }={}) => {

	let tabables = Array.from(focusBase.querySelectorAll(queryString));

	if (!tabables.includes(target)) {
		target.dataset.focusCurrentTarget = true;
		tabables = Array.from(focusBase.querySelectorAll(`${queryString}, [data-focus-current-target]`));
		delete target.dataset.focusCurrentTarget;
	}

	let currentIndex = tabables.indexOf(target);
	let nextIndex = shiftKey ? currentIndex - 1 : currentIndex + 1;

	if (!circular && (nextIndex < 0 || nextIndex >= tabables.length)) {
		if (nextBoundary && !shiftKey) {
			nextBoundary.focusFirst();
		} else if (previousBoundary && shiftKey) {
			previousBoundary.focusLast();
		} else {
			// Hand over focus control to parent focusBoundary.
			window.focusNext({ shiftKey, target, focusBase });
		}
		return;
	}

	let nextTarget;
	if (currentIndex === -1) {
		nextTarget = shiftKey ? tabables[tabables.length - 1] : tabables[0];
	} else {
		// Most typical scenario.
		nextIndex = modulo(nextIndex, tabables.length);
		nextTarget = tabables[nextIndex];

		if (!shiftKey && nextTarget.dataset.focusNoEntryFirst) {
			let newTarget = tabables[nextIndex + Number(nextTarget.dataset.focusTabableLength) - 1];
			focusBase.focusNext({ shiftKey, target: newTarget });
			return;
		}
		if (shiftKey && nextTarget.dataset.focusNoEntryLast) {
			let newTarget = tabables[nextIndex - Number(nextTarget.dataset.focusTabableLength) + 1];
			focusBase.focusNext({ shiftKey, target: newTarget });
			return;
		}
	}

	if (nextTarget) {
		nextTarget.focus();
		if (document.activeElement !== nextTarget) {

			// Sometimes we cannot know when an element is ufocusable.
			// This is especially true for descendants of elements with display: none.
			// In such cases we simply move on to the next element.
			// If there is a huge tree with a v-show consider putting this in a noEntry focus boundary.
			focusBase.focusNext({ shiftKey, target: nextTarget });
		}
	} else {
		console.log('Reached end of focusNext with no nextTarget!');
	}
};

// focusBase should be bound to the DOM-element that defines the focus boundary.
const unbound_focusFirst = focusBase => {
	let firstTabable = focusBase.querySelector(queryString);
	if (firstTabable) {
		firstTabable.focus();
		if (document.activeElement !== firstTabable) {
			focusBase.focusNext({ target: firstTabable });
		}
	}
};

// focusBase should be bound to the DOM-element that defines the focus boundary.
const unbound_focusLast = focusBase => {
	let tabables = Array.from(focusBase.querySelectorAll(queryString));
	if (!tabables.length) return;

	tabables[tabables.length - 1].focus();
	if (document.activeElement !== tabables[tabables.length - 1]) {
		focusBase.focusNext({ shiftKey: true, target: tabables[tabables.length - 1] });
	}
};

// A global focusNext is used to easily find parent focus boundary.
window.focusNext = options => {
	let current;
	if (options) {
		current = options.focusBase || options.target || document.activeElement;
	} else {
		current = document.activeElement;
	}
	do {
		if (current.tagName === 'BODY') {
			// If this happens it's usually because a focused element is destroyed,
			// which causes the body to get focus instead.
			return;
		}
		current = current.parentElement;
	} while (!current.focusNext);
	current.focusNext(options);
};

const registerBoundary = (id, el) => {
	if (window.__focusBoundaries[id]) {
		console.error(`registerBoundary: FocusBoundary ${id} allready exists`);
		return;
	}
	window.__focusBoundaries[id] = el;
};

const unregisterBoundary = id => {
	if (!window.__focusBoundaries[id]) {
		console.error(`unregisterBoundary: FocusBoundary ${id} does not exists`);
		return;
	}
	delete window.__focusBoundaries[id];
};

const getAdjacentBoundaries = (prev, next) => {
	let previousBoundary; let nextBoundary;
	if (prev) {
		if (window.__focusBoundaries[prev]) {
			previousBoundary = window.__focusBoundaries[prev];
		} else {
			console.error(`getAdjacentBoundaries: prev is ${prev} but no window.__focusBoundaries[prev] is ${window.__focusBoundaries[prev]}`);
		}
	}
	if (next) {
		if (window.__focusBoundaries[next]) {
			nextBoundary = window.__focusBoundaries[next];
		} else {
			console.error(`getAdjacentBoundaries: next is ${next} but no window.__focusBoundaries[next] is ${window.__focusBoundaries[next]}`);
		}
	}
	return { previousBoundary, nextBoundary };
};

const setNoEntryAttributes = focusBase => {
	let tabables = Array.from(focusBase.querySelectorAll(queryString));
	if (tabables.length) {
		tabables[0].dataset.focusNoEntryFirst = true;
		tabables[0].dataset.focusTabableLength = tabables.length;
		tabables[tabables.length - 1].dataset.focusNoEntryLast = true;
		tabables[tabables.length - 1].dataset.focusTabableLength = tabables.length;
	} else {
		console.log('A focusBoundary.noEntry with no tabables has been defined!');
	}
};

// focusBase should be bound to the DOM-element that defines the focus boundary.
const onKeyDown = (focusBase, event) => {
	switch (event.key) {
	case 'Tab':
		onTabDown(focusBase, event);
		break;
	case 'Enter':
		onEnterDown(focusBase, event);
		break;
	default:
	}
};

const onTabDown = (focusBase, event) => {
	event.preventDefault();
	event.stopPropagation();
	focusBase.focusNext(event);
};

const enterIsTabInputTypes = ['text', 'number'];

const onEnterDown = (focusBase, event) => {
	if (event.ctrlKey || event.altKey) return;

	// Any more elements that need tab behaviour on enter?
	if (event.target.tagName === 'INPUT' && enterIsTabInputTypes.includes(event.target.type)) {
		event.preventDefault();
		event.stopPropagation();
		focusBase.focusNext(event);
	}
};